import React, { useState, useEffect } from 'react';
import './ActivityTab.css';

interface Activity {
  type: 'payment_success' | 'payment_failed' | 'draft_created' | 'manual_confirm' | 'cancelled' | 'hotel_booking' | 'breakfast_booking' | 'dinner_booking' | 'asistpro_conflict';
  icon: string;
  statusColor: string;
  source?: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  bookingId: string;
  reservationCode: string;
  totalPrice: number;
  status: string;
  paymentStatus: string;
  timestamp: string;
  details: string;
}

const ActivityTab: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<24 | 72 | 168 | 720>(24); // 24h, 3d, 7d, 30d
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('Token bulunamadı');
        return;
      }

      const response = await fetch(`/api/admin/activity?hours=${timeFilter}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Aktivite verileri alınamadı');
      }

      const data = await response.json();
      setActivities(data.activities || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [timeFilter]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchActivities();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, timeFilter]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityTypeLabel = (type: Activity['type']): string => {
    const labels = {
      payment_success: 'Ödeme Başarılı',
      payment_failed: 'Ödeme Başarısız',
      draft_created: 'Rezervasyon Taslağı',
      manual_confirm: 'Manuel Onay',
      cancelled: 'İptal Edildi',
      hotel_booking: 'Otel Rezervasyonu',
      breakfast_booking: 'Kahvaltı Rezervasyonu',
      dinner_booking: 'Akşam Yemeği Rezervasyonu',
      asistpro_conflict: 'AsistPro Çakışma'
    };
    return labels[type] || type;
  };

  const getSourceLabel = (source?: string): string => {
    const s = (source || '').trim();
    if (!s) return 'website';

    const labels: Record<string, string> = {
      'home-calendar': 'Ana Sayfa Takvim',
      'reservation-page': 'Rezervasyon Sayfası',
      'ourrooms-summary': 'Odalarımız Ozet',
      'ourrooms-room': 'Odalarımız Oda',
      'ourrooms': 'Odalarımız',
      'website': 'Website',
      'admin': 'Admin'
    };

    return labels[s] || s;
  };

  if (loading) {
    return (
      <div className="activity-tab">
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-tab">
        <div className="error-message">{error}</div>
        <button onClick={fetchActivities} className="retry-button">
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="activity-tab">
      <div className="activity-header">
        <h2>📊 Sistem Aktivitesi</h2>
        <div className="activity-controls">
          <div className="time-filter">
            <label>Zaman Aralığı:</label>
            <select value={timeFilter} onChange={(e) => setTimeFilter(Number(e.target.value) as any)}>
              <option value={24}>Son 24 Saat</option>
              <option value={72}>Son 3 Gün</option>
              <option value={168}>Son 7 Gün</option>
              <option value={720}>Son 30 Gün</option>
            </select>
          </div>
          <div className="auto-refresh-toggle">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Otomatik Yenile (30sn)
            </label>
          </div>
          <button onClick={fetchActivities} className="refresh-button">
            🔄 Yenile
          </button>
        </div>
      </div>

      <div className="activity-stats">
        <div className="stat-card">
          <span className="stat-icon">📊</span>
          <div className="stat-content">
            <div className="stat-value">{activities.length}</div>
            <div className="stat-label">Toplam Aktivite</div>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">✅</span>
          <div className="stat-content">
            <div className="stat-value">
              {activities.filter(a => a.type === 'payment_success').length}
            </div>
            <div className="stat-label">Başarılı Ödeme</div>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">❌</span>
          <div className="stat-content">
            <div className="stat-value">
              {activities.filter(a => a.type === 'payment_failed').length}
            </div>
            <div className="stat-label">Başarısız Ödeme</div>
          </div>
        </div>
        <div className="stat-card">
          <span className="stat-icon">⏳</span>
          <div className="stat-content">
            <div className="stat-value">
              {activities.filter(a => a.type === 'draft_created').length}
            </div>
            <div className="stat-label">Taslak</div>
          </div>
        </div>
      </div>

      <div className="activity-timeline">
        {activities.length === 0 ? (
          <div className="no-activities">
            Bu zaman aralığında aktivite bulunamadı.
          </div>
        ) : (
          activities.map((activity, index) => (
            <div key={index} className={`activity-item ${activity.type}`}>
              <div className="activity-icon" style={{ backgroundColor: activity.statusColor }}>
                {activity.icon}
              </div>
              <div className="activity-content">
                <div className="activity-main">
                  <div className="activity-title">
                    <span className="activity-type">{getActivityTypeLabel(activity.type)}</span>
                    <span
                      style={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: '#475569',
                        background: '#e2e8f0',
                        borderRadius: '999px',
                        padding: '2px 8px'
                      }}
                    >
                      Kaynak: {getSourceLabel(activity.source)}
                    </span>
                    {activity.reservationCode && activity.reservationCode !== 'N/A' && (
                      <span className="reservation-code">{activity.reservationCode}</span>
                    )}
                  </div>
                  <div className="activity-customer">
                    <span className="customer-name">👤 {activity.customerName}</span>
                    {activity.customerEmail && (
                      <span className="customer-email">✉️ {activity.customerEmail}</span>
                    )}
                    {activity.customerPhone && (
                      <span className="customer-phone">📞 {activity.customerPhone}</span>
                    )}
                  </div>
                  {activity.details && (
                    <div className="activity-details">{activity.details}</div>
                  )}
                </div>
                <div className="activity-meta">
                  <span className="activity-price">
                    💰 {activity.totalPrice.toFixed(2)} TL
                  </span>
                  <span className="activity-time">{formatDate(activity.timestamp)}</span>
                  <span className="booking-id">ID: {activity.bookingId}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityTab;
